import { z } from 'zod';

/**
 * What one provider call consumed, in the four classes vendors actually bill.
 *
 * Two numbers are not enough, and the reason is a real trap: on the Anthropic
 * API `usage.input_tokens` is the **uncached remainder only** — the true prompt
 * size is `input_tokens + cache_creation_input_tokens + cache_read_input_tokens`.
 * A cost model carrying only "input" and "output" therefore *under*-counts the
 * moment prompt caching appears anywhere in an adapter, which is the one
 * direction a budget guard must never fail in. All four are recorded, all four
 * are priced (`ai.pricing`), and an adapter whose vendor does not distinguish
 * them reports zero for the two it cannot know.
 *
 * These are the **provider's** numbers wherever the provider reports them — an
 * estimate that calls itself usage is how a month of spend goes unexplained.
 * Where a vendor genuinely reports nothing (Gemini's embedding endpoint returns
 * no token counts at all), the adapter derives a deliberately **high** figure
 * from the input and says so in its own documentation: an over-count costs an
 * early refusal, while the alternative — reporting zero — would make that
 * capability free to the budget guard, which is the one thing it may never be.
 */
export const AiTokenUsageSchema = z
  .object({
    /** Prompt tokens billed at the full input rate. */
    inputTokens: z.number().int().min(0),
    /** Generated tokens (thinking included — vendors bill it as output). */
    outputTokens: z.number().int().min(0),
    /** Prompt tokens served from the vendor's prompt cache, billed at ~0.1x. */
    cachedInputTokens: z.number().int().min(0),
    /** Prompt tokens written to the vendor's prompt cache, billed at 1.25–2x. */
    cacheWriteTokens: z.number().int().min(0),
  })
  .strict();

export type AiTokenUsage = z.infer<typeof AiTokenUsageSchema>;

/** The all-zero usage a cache hit records — it consumed nothing at the vendor. */
export const NO_TOKEN_USAGE: AiTokenUsage = Object.freeze({
  inputTokens: 0,
  outputTokens: 0,
  cachedInputTokens: 0,
  cacheWriteTokens: 0,
});
